import { TaskResponse, TaskUiElementsResponse } from "@9amhealth/openapi";
import { useBlocNext } from "@blac/react";
import { Blac } from "blac-next";
import type { FC } from "react";
import React from "react";
import OnboardingBloc from "src/state/OnboardingBloc/OnboardingBloc";
import TaskManagementBloc, {
  TaskKey
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import type { TaskResponseKnown } from "src/state/TaskManagementBloc/TaskManagementBloc";
import { getStatusBadgeVariant } from "./debugTaskUtils";
import {
  DebugBadge,
  DebugButton,
  DebugButtonRow,
  DebugDivider,
  DebugFormGroup,
  DebugKeyValue,
  DebugToggleSwitch
} from "./debug.styles";
import DebugInitialLabsCubit, {
  LAB_ORDER_PROVIDERS,
  TASK_CONFIGS,
  stateFromTask,
  type MockTaskState,
  type TaskConfig
} from "./DebugInitialLabsCubit";
import DebugSection from "./DebugSection";

const ALL_STATUSES = Object.values(TaskResponse.status);
const ALL_ICON_TYPES = Object.values(TaskUiElementsResponse.iconType);

const TaskMockEditor: FC<{
  config: TaskConfig;
  liveTask: TaskResponseKnown | undefined;
  mockState: MockTaskState;
  cubit: DebugInitialLabsCubit;
}> = ({ config, liveTask, mockState, cubit }) => {
  const update = (patch: Partial<MockTaskState>) =>
    cubit.updateMockState(config.slug, { ...mockState, ...patch });

  return (
    <DebugSection title={config.label} defaultOpen={false}>
      <DebugToggleSwitch>
        <span className="label">Mock Enabled</span>
        <input
          type="checkbox"
          checked={mockState.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
        />
        <span className="switch" />
      </DebugToggleSwitch>

      <DebugKeyValue>
        <span className="key">Slug</span>
        <span className="value mono">{config.slug}</span>
      </DebugKeyValue>

      <DebugKeyValue>
        <span className="key">Live Status</span>
        <span className="value">
          {liveTask ? (
            <DebugBadge variant={getStatusBadgeVariant(liveTask.status)}>
              {liveTask.status}
            </DebugBadge>
          ) : (
            <DebugBadge variant="danger">Not Found</DebugBadge>
          )}
        </span>
      </DebugKeyValue>

      {liveTask && !mockState.enabled && (
        <DebugButtonRow style={{ marginTop: "0.5rem" }}>
          <DebugButton
            className="small"
            onClick={() =>
              cubit.updateMockState(config.slug, stateFromTask(liveTask))
            }
          >
            Copy Live to Mock
          </DebugButton>
        </DebugButtonRow>
      )}

      {mockState.enabled && (
        <div style={{ marginTop: "0.5rem" }}>
          <DebugFormGroup>
            <label>Status</label>
            <select
              value={mockState.status}
              onChange={(e) =>
                update({ status: e.target.value as TaskResponse.status })
              }
            >
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </DebugFormGroup>

          {config.slug === TaskKey.MEDICAL_INSURANCE && (
            <DebugToggleSwitch>
              <span className="label">photoUploadCanBeSkippedByUser</span>
              <input
                type="checkbox"
                checked={
                  mockState.medicalInsurance.photoUploadCanBeSkippedByUser
                }
                onChange={(e) =>
                  update({
                    medicalInsurance: {
                      ...mockState.medicalInsurance,
                      photoUploadCanBeSkippedByUser: e.target.checked
                    }
                  })
                }
              />
              <span className="switch" />
            </DebugToggleSwitch>
          )}

          {config.slug === TaskKey.COMPLETE_INITIAL_LAB_ORDER && (
            <>
              <DebugFormGroup>
                <label>Lab Order Provider</label>
                <select
                  value={mockState.labOrder.labOrderProvider}
                  onChange={(e) =>
                    update({
                      labOrder: {
                        ...mockState.labOrder,
                        labOrderProvider: e.target.value
                      }
                    })
                  }
                >
                  {LAB_ORDER_PROVIDERS.map((p) => (
                    <option key={p || "_none"} value={p}>
                      {p || "(none)"}
                    </option>
                  ))}
                </select>
              </DebugFormGroup>

              <DebugFormGroup>
                <label>Requisition File ID</label>
                <input
                  type="text"
                  value={mockState.labOrder.requisitionFileId}
                  onChange={(e) =>
                    update({
                      labOrder: {
                        ...mockState.labOrder,
                        requisitionFileId: e.target.value
                      }
                    })
                  }
                  placeholder="(empty)"
                />
              </DebugFormGroup>

              <DebugFormGroup>
                <label>Appointment URL</label>
                <input
                  type="text"
                  value={mockState.labOrder.appointmentUrl}
                  onChange={(e) =>
                    update({
                      labOrder: {
                        ...mockState.labOrder,
                        appointmentUrl: e.target.value
                      }
                    })
                  }
                  placeholder="(empty)"
                />
              </DebugFormGroup>

              <DebugFormGroup>
                <label>Appointment ID</label>
                <input
                  type="text"
                  value={mockState.labOrder.appointmentId}
                  onChange={(e) =>
                    update({
                      labOrder: {
                        ...mockState.labOrder,
                        appointmentId: e.target.value
                      }
                    })
                  }
                  placeholder="(empty)"
                />
              </DebugFormGroup>

              <DebugFormGroup>
                <label>PSC Location Name</label>
                <input
                  type="text"
                  value={mockState.labOrder.labOrderPscLocationName}
                  onChange={(e) =>
                    update({
                      labOrder: {
                        ...mockState.labOrder,
                        labOrderPscLocationName: e.target.value
                      }
                    })
                  }
                  placeholder="(empty)"
                />
              </DebugFormGroup>
            </>
          )}

          <details>
            <summary
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: "#666",
                cursor: "pointer",
                padding: "0.5rem 0"
              }}
            >
              Other Config
            </summary>

            <DebugFormGroup>
              <label>Title</label>
              <input
                type="text"
                value={mockState.title}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="Task title"
              />
            </DebugFormGroup>

            <DebugFormGroup>
              <label>Icon Type</label>
              <select
                value={mockState.iconType}
                onChange={(e) =>
                  update({
                    iconType: e.target.value as TaskUiElementsResponse.iconType
                  })
                }
              >
                {ALL_ICON_TYPES.map((icon) => (
                  <option key={icon} value={icon}>
                    {icon}
                  </option>
                ))}
              </select>
            </DebugFormGroup>

            <DebugFormGroup>
              <label>Popup Type</label>
              <input
                type="text"
                value={mockState.popupType}
                onChange={(e) => update({ popupType: e.target.value })}
                placeholder="undefined"
              />
            </DebugFormGroup>

            <DebugDivider />

            <div
              style={{
                fontSize: "11px",
                fontWeight: 600,
                marginBottom: "0.5rem",
                color: "#666"
              }}
            >
              Behavior
            </div>

            <DebugToggleSwitch>
              <span className="label">userCanSee</span>
              <input
                type="checkbox"
                checked={mockState.userCanSee}
                onChange={(e) => update({ userCanSee: e.target.checked })}
              />
              <span className="switch" />
            </DebugToggleSwitch>

            <DebugToggleSwitch>
              <span className="label">userCanSkip</span>
              <input
                type="checkbox"
                checked={mockState.userCanSkip}
                onChange={(e) => update({ userCanSkip: e.target.checked })}
              />
              <span className="switch" />
            </DebugToggleSwitch>

            <DebugToggleSwitch>
              <span className="label">adminCanSkip</span>
              <input
                type="checkbox"
                checked={mockState.adminCanSkip}
                onChange={(e) => update({ adminCanSkip: e.target.checked })}
              />
              <span className="switch" />
            </DebugToggleSwitch>

            <DebugToggleSwitch>
              <span className="label">adminCanComplete</span>
              <input
                type="checkbox"
                checked={mockState.adminCanComplete}
                onChange={(e) => update({ adminCanComplete: e.target.checked })}
              />
              <span className="switch" />
            </DebugToggleSwitch>
          </details>
        </div>
      )}
    </DebugSection>
  );
};

const InitialLabsConsolidatedDisplay: FC = () => {
  const [{ tasks }, taskBloc] = useBlocNext(TaskManagementBloc);

  const consolidatedLabsTask = taskBloc.consolidatedTasks.find(
    (t) => t.slug === TaskKey.ONBOARDING_LABS_CONSOLIDATED
  );

  const { labsStepStatus, labsTaskAction } = React.useMemo(() => {
    try {
      const onboardingBloc = Blac.getBloc(OnboardingBloc);
      return {
        labsStepStatus: onboardingBloc.statusStepGetYourLabsDone(),
        labsTaskAction: onboardingBloc.getLabsTaskAction().action
      };
    } catch {
      return { labsStepStatus: null, labsTaskAction: null };
    }
  }, [tasks]);

  return (
    <div style={{ marginTop: "0.75rem" }}>
      <DebugKeyValue>
        <span className="key">Labs Step</span>
        <span className="value">
          <DebugBadge
            variant={getStatusBadgeVariant(labsStepStatus ?? undefined)}
          >
            {labsStepStatus ?? "unknown"}
          </DebugBadge>
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Labs Action</span>
        <span className="value mono">{labsTaskAction ?? "unknown"}</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Consolidated</span>
        <span className="value">
          <DebugBadge variant={consolidatedLabsTask ? "success" : "danger"}>
            {consolidatedLabsTask ? consolidatedLabsTask.status : "Not Found"}
          </DebugBadge>
        </span>
      </DebugKeyValue>
    </div>
  );
};

const DebugInitialLabs: FC = () => {
  const [{ mockStates }, cubit] = useBlocNext(DebugInitialLabsCubit);
  const [{ tasks }] = useBlocNext(TaskManagementBloc);

  React.useEffect(() => {
    cubit.init();
    return () => cubit.cleanup();
  }, []);

  return (
    <div>
      {TASK_CONFIGS.map((config) => (
        <TaskMockEditor
          key={config.slug}
          config={config}
          liveTask={tasks.find((t) => t.slug === config.slug)}
          mockState={mockStates[config.slug]}
          cubit={cubit}
        />
      ))}

      <InitialLabsConsolidatedDisplay />
    </div>
  );
};

export default DebugInitialLabs;
